import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getSectionRepeat, rowLabel } from '@chordbaker/music-theory';
import { RowState, SectionState } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { rowCountLabel, rowText, titleOf, type ReorderStyles } from './chartReorderShared';
import SectionRepeatPill from './SectionRepeatPill';
import DragHandleIcon from './icons/DragHandleIcon';
import DuplicateIcon from './icons/DuplicateIcon';
import TrashIcon from './icons/TrashIcon';
import ChevronLeftIcon from './icons/ChevronLeftIcon';

interface ItemActionsProps {
  keyId: string;
  name: string;
  what: 'section' | 'row';
  canDelete: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  styles: ReorderStyles;
}

/** Duplicate and delete, as a pair at the end of a section header or row. */
const ItemActions: React.FC<ItemActionsProps> = ({ keyId, name, what, canDelete, onDuplicate, onDelete, styles }) => {
  const { theme } = useTheme();
  return (
    <span style={styles.rowButtons}>
      <button
        type="button"
        onClick={onDuplicate}
        style={styles.rowButton}
        title={`Duplicate ${what}`}
        aria-label={`Duplicate ${name}`}
        data-testid={`section-reorder-duplicate-${keyId}`}
      >
        <DuplicateIcon size={16} />
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={!canDelete}
        style={{ ...styles.rowButton, ...(canDelete ? {} : styles.rowButtonDisabled) }}
        title={canDelete ? `Delete ${what}` : what === 'section' ? 'The chart needs at least one section' : 'A section needs at least one row'}
        aria-label={`Delete ${name}`}
        data-testid={`section-reorder-delete-${keyId}`}
      >
        <TrashIcon size={16} color={canDelete ? theme.colors.error : theme.colors.textTertiary} />
      </button>
    </span>
  );
};

interface SectionHeaderItemProps {
  id: string;
  keyId: string;
  section: SectionState;
  isCopy: boolean;
  rowCount: number;
  expanded: boolean;
  position: number;
  count: number;
  canDelete: boolean;
  onToggle: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  styles: ReorderStyles;
}

/**
 * A section in the sheet. Its title area shows or hides its rows. It drags
 * only while collapsed: while expanded, its rows are what you move.
 */
export const SectionHeaderItem: React.FC<SectionHeaderItemProps> = ({
  id,
  keyId,
  section,
  isCopy,
  rowCount,
  expanded,
  position,
  count,
  canDelete,
  onToggle,
  onDuplicate,
  onDelete,
  styles,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: expanded });
  const repeat = getSectionRepeat(section);
  const rows = rowCountLabel(rowCount);
  const name = `${titleOf(section)}${isCopy ? ' (copy)' : ''}`;

  return (
    <li
      ref={setNodeRef}
      {...listeners}
      style={{
        ...styles.item,
        ...(expanded ? styles.itemStatic : {}),
        ...(isDragging ? styles.itemActive : {}),
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      data-testid={`section-reorder-item-${keyId}`}
    >
      {/* The grip is the keyboard target: Space picks the section up, arrows move it */}
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        style={{ ...styles.handle, ...(expanded ? styles.handleDisabled : {}) }}
        aria-label={`${titleOf(section)}${isCopy ? ', copy' : ''}, ${rows}, position ${position} of ${count}`}
        data-testid={`section-reorder-handle-${keyId}`}
      >
        <DragHandleIcon size={20} />
      </span>
      <button
        type="button"
        onClick={onToggle}
        style={styles.toggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Hide' : 'Show'} the rows of ${titleOf(section)}`}
        data-testid={`section-reorder-toggle-${keyId}`}
      >
        <span style={{ ...styles.title, ...(section.title ? {} : styles.untitled) }}>{titleOf(section)}</span>
        {repeat !== null && (
          <span style={styles.pill}>
            <SectionRepeatPill repeat={repeat} />
          </span>
        )}
        {isCopy && <span style={styles.copyTag}>Copy</span>}
        <span style={styles.rowCount}>{rows}</span>
        <ChevronLeftIcon
          size={16}
          style={{ ...styles.chevron, transform: `rotate(${expanded ? -90 : 180}deg)` }}
        />
      </button>
      <ItemActions
        keyId={keyId}
        name={name}
        what="section"
        canDelete={canDelete}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        styles={styles}
      />
    </li>
  );
};

interface RowItemProps {
  id: string;
  keyId: string;
  row: RowState;
  isCopy: boolean;
  sectionTitle: string;
  position: number;
  canDelete: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  styles: ReorderStyles;
}

/** A row of an expanded section: its chords on one line (else its lyrics). */
export const RowItem: React.FC<RowItemProps> = ({
  id,
  keyId,
  row,
  isCopy,
  sectionTitle,
  position,
  canDelete,
  onDuplicate,
  onDelete,
  styles,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const label = rowLabel(row);
  const text = rowText(row);

  return (
    <li
      ref={setNodeRef}
      {...listeners}
      style={{
        ...styles.item,
        ...styles.rowItem,
        ...(isDragging ? styles.itemActive : {}),
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      data-testid={`section-reorder-item-${keyId}`}
    >
      <span
        ref={setActivatorNodeRef}
        {...attributes}
        style={styles.handle}
        aria-label={`Row ${position} of ${sectionTitle}${isCopy ? ', copy' : ''}: ${text}`}
        data-testid={`section-reorder-handle-${keyId}`}
      >
        <DragHandleIcon size={20} />
      </span>
      <span
        style={{
          ...styles.rowLabel,
          ...(label.kind === 'lyrics' ? styles.rowLabelLyrics : {}),
          ...(label.kind === 'empty' ? styles.untitled : {}),
        }}
      >
        {text}
      </span>
      {isCopy && <span style={styles.copyTag}>Copy</span>}
      <ItemActions
        keyId={keyId}
        name={`row ${position} of ${sectionTitle}`}
        what="row"
        canDelete={canDelete}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        styles={styles}
      />
    </li>
  );
};
